import { useEffect, useState } from 'react';
import { Button, Notice } from './ui';
import { Dialog } from './Dialog';
import { ApiError } from '../lib/api';
import { useScoreMode, type Direction, type ScoreMode } from '../lib/hub';
import './ScoreMode.css';

/**
 * How an organisation uses scores (backend services/scoreMode.ts). The owner
 * chooses in the welcome steps and organisation settings; everyone else is
 * told when it changes.
 */
export const SCORE_MODE_OPTIONS: { mode: ScoreMode; title: string; note?: string; teachers: string; leaders: string }[] = [
  {
    mode: 'full',
    title: 'Full scores',
    teachers: 'See their scores on every lesson.',
    leaders: 'See anonymous team averages, each teacher’s score trend from the lessons they share with leaders, and scores on shared reports.',
  },
  {
    mode: 'team',
    title: 'Team scores',
    note: 'Most organisations start here',
    teachers: 'See their scores on every lesson.',
    leaders: 'See anonymous team averages and the reports teachers share, with scores. No individual score trends.',
  },
  {
    mode: 'trends',
    title: 'Trends only',
    teachers: 'See strengths, next steps and whether each area is improving, stable or dipping. No numbers.',
    leaders: 'See the team picture the same way, and shared reports without scores.',
  },
];

export const scoreModeTitle = (m: ScoreMode) => SCORE_MODE_OPTIONS.find((o) => o.mode === m)?.title ?? m;

/** What's true whichever mode is chosen. */
export function ScoreModeAlways() {
  return (
    <p className="faint sm-always">
      Whichever you choose: leaders only read the reports teachers share. Every lesson counts towards team figures, but only anonymously: each figure
      needs at least five teachers behind it (not counting the leader looking), nobody is named or ranked, and filters that could single someone out
      are switched off. Every lesson is still marked against your framework, so trends stay accurate if you change this later.
    </p>
  );
}

/** The three choices as radio cards, each saying what teachers and leaders see. */
export function ScoreModeOptions({
  value,
  onChange,
  disabled,
}: {
  value: ScoreMode | null;
  onChange: (m: ScoreMode) => void;
  disabled?: boolean;
}) {
  return (
    <div className="sm-cards" role="radiogroup" aria-label="How scores are shown">
      {SCORE_MODE_OPTIONS.map((o) => (
        <button
          key={o.mode}
          type="button"
          role="radio"
          aria-checked={value === o.mode}
          className="sm-card"
          disabled={disabled}
          onClick={() => onChange(o.mode)}
        >
          <span className="sm-radio" aria-hidden="true" />
          <span>
            <span className="sm-title">
              {o.title}
              {o.note ? <span className="chip chip-accent">{o.note}</span> : null}
            </span>
            <dl className="sm-who">
              <dt>Teachers</dt>
              <dd>{o.teachers}</dd>
              <dt>Leaders</dt>
              <dd>{o.leaders}</dd>
            </dl>
          </span>
        </button>
      ))}
    </div>
  );
}

/** Organisation settings: choose, then confirm (everyone is told). Read-only for leaders who aren't owners. */
export function ScoreModeSetting({ mode, canChange }: { mode: ScoreMode; canChange: boolean }) {
  const save = useScoreMode();
  const [picked, setPicked] = useState<ScoreMode>(mode);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const changed = picked !== mode;
  // Follow the saved mode when it loads or changes.
  useEffect(() => setPicked(mode), [mode]);

  const apply = async () => {
    setError(null);
    try {
      await save.mutateAsync(picked);
      setConfirming(false);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    }
  };

  return (
    <>
      <ScoreModeOptions
        value={picked}
        onChange={(m) => {
          setDone(false);
          setPicked(m);
        }}
        disabled={!canChange}
      />
      <ScoreModeAlways />
      {!canChange ? <p className="faint">Only an owner of your organisation can change this.</p> : null}
      {done && !changed ? <Notice tone="success">Changed to {scoreModeTitle(mode)}. Everyone in your organisation has been told.</Notice> : null}
      {canChange && changed ? (
        <div className="sm-actions">
          <Button onClick={() => setConfirming(true)}>Change to {scoreModeTitle(picked)}</Button>
          <Button variant="ghost" onClick={() => setPicked(mode)}>
            Keep {scoreModeTitle(mode)}
          </Button>
        </div>
      ) : null}
      <Dialog open={confirming} onClose={() => setConfirming(false)} title={`Change to ${scoreModeTitle(picked)}?`}>
        <p className="muted">
          {picked === 'trends'
            ? 'Teachers’ reports will show strengths, next steps and improving, stable or dipping instead of numbers, and leaders will see the team picture the same way.'
            : picked === 'full'
              ? 'Teachers keep seeing their scores. Leaders will also see each teacher’s score trend from the lessons they share with them.'
              : 'Teachers see their scores. Leaders see team averages and shared reports, but not individual score trends.'}{' '}
          Everyone in your organisation is told in the app, and can see which it is on their Sharing screen.
        </p>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Keep as is
          </Button>
          <Button onClick={apply} loading={save.isPending}>
            Change and tell everyone
          </Button>
        </div>
      </Dialog>
    </>
  );
}

const DIR_LABEL: Record<Direction, string> = { improving: '↗ Improving', stable: '→ Stable', dipping: '↘ Dipping' };

/** Improving / stable / dipping, as a small tag. */
export function DirectionTag({ d, large }: { d: Direction | null | undefined; large?: boolean }) {
  if (!d) return null;
  return <span className={`dir-tag is-${d}${large ? ' dir-tag-lg' : ''}`}>{DIR_LABEL[d]}</span>;
}
